import { Injectable } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import {
    BatteryService,
    BatterySetService,
    LocationService,
    PersonsOrganisationDto
} from '@flyfreely-portal-ui/flyfreely';

import { LotamDialoguesService } from '@flyfreely-portal-ui/notices';
import { BatteryDialoguesService } from 'libs/batteries/src/lib/battery-dialogues.service';
import { EquipmentDialoguesService } from 'libs/equipment/src/lib/equipment-dialogues.service';
import { LocationDialoguesService } from 'libs/locations/src/lib/location-dialogues.service';
import { MaintenanceDialogues } from 'libs/maintenance/src/lib/maintenance-dialogues.service';
import { MissionDialoguesService } from 'libs/missions/src/lib/mission-dialogues.service';
import { RpaDialoguesService } from 'libs/rpa/src/lib/rpa-dialogues.service';
import { filter, Subject, takeUntil } from 'rxjs';
import { CurrentOrganisation, WorkspaceStateService } from '..';

/**
 * This service is responsible for handling the navigation to the short links we use in notification, maps, and emails.
 */
@Injectable({
    providedIn: 'root'
})
export class WorkspaceNavigationService {
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private missionDialoguesService: MissionDialoguesService,
        private rpaDialoguesService: RpaDialoguesService,
        private batteryService: BatteryService,
        private batterySetService: BatterySetService,
        private batteryDialoguesService: BatteryDialoguesService,
        private equipmentDialoguesService: EquipmentDialoguesService,
        private locationService: LocationService,
        private locationDialoguesService: LocationDialoguesService,
        private maintenanceDialoguesService: MaintenanceDialogues,
        private lotamDialoguesService: LotamDialoguesService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private workspaceStateService: WorkspaceStateService
    ) {}

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    /**
     * Navigate to, or show the dialogue for the entity identified in the link
     * @param link a link string
     */
    navigateToLink(link: string) {
        this.workspaceStateService.currentOrganisation$
            .pipe(filter(o => o.type === 'organisation_loaded'))
            .subscribe((managingOrganisation: CurrentOrganisation) =>
                this.doNavigation(link, managingOrganisation.organisation)
            );
    }

    doNavigation(link: string, managingOrganisation: PersonsOrganisationDto) {
        const component = link.split('/')[0];
        const param = link.split('/').length > 1 ? link.split('/')[1] : null;
        switch (component) {
            case 'missions':
                this.missionDialoguesService.showMissionDetails(
                    parseInt(param, 10),
                    true
                );
                break;

            case 'tasks':
                this.router.navigate(['tasks', component, param], {
                    relativeTo: this.activatedRoute,
                    queryParamsHandling: 'preserve'
                });
                break;

            case 'rpa':
                this.rpaDialoguesService.showCraftDetails(
                    parseInt(param, 10),
                    managingOrganisation.id,
                    true
                );
                break;

            case 'batteries':
                this.batteryService
                    .findBattery(parseInt(param, 10))
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe(battery => {
                        this.batteryDialoguesService.showBatteryDetailsDialogue(
                            battery,
                            managingOrganisation
                        );
                    });
                break;

            case 'battery-sets':
                this.batterySetService
                    .findBatterySet(parseInt(param, 10))
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe(batterySet => {
                        this.batteryDialoguesService.showBatterySetDetailsDialogue(
                            batterySet,
                            managingOrganisation
                        );
                    });
                break;

            case 'equipment':
                this.equipmentDialoguesService.showEquipmentDetails(
                    parseInt(param, 10),
                    managingOrganisation,
                    true
                );
                break;

            case 'locations':
                this.locationService
                    .findLocation(parseInt(param, 10), managingOrganisation.id)
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe(location => {
                        this.locationDialoguesService.showLocation(
                            location,
                            managingOrganisation.id
                        );
                    });
                break;

            case 'maintenance':
                this.maintenanceDialoguesService.showMaintenanceDetails(
                    managingOrganisation.id,
                    parseInt(param, 10)
                );
                break;

            case 'orgadmin':
                // TODO router test
                this.router.navigate([component], {
                    queryParams: {
                        tab: param ?? 'details',
                        ...this.activatedRoute.snapshot.queryParams
                    }
                });
                break;

            case 'profile':
            case 'userprofile':
                // TODO ROUTER test
                this.router.navigateByUrl('/userprofile');
                break;

            case 'notices':
                this.lotamDialoguesService.showLotamDetailDialogue(
                    parseInt(param, 10),
                    managingOrganisation.id
                );
                break;

            default:
                if (link !== '') {
                    try {
                        this.router.navigate([link], {
                            queryParams: {
                                organisationId: managingOrganisation.id
                            }
                        });
                    } catch (e: any) {
                        // the route may not exist
                        console.warn({ e });
                    }
                }
                break;
        }
    }
}
